/**
 * BoekhoudingLayout - Morris-style layout met sidebar
 * Gebaseerd op Morris base.html
 */

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from '../../styles/Boekhouding.module.css';
import '../../styles/boekhouding-global.css';
import { BOEKJAAR_OPTIES } from '../../services/boekhouding.service';

interface BoekhoudingLayoutProps {
  children: ReactNode;
  title: string;
  headerActions?: ReactNode;
}

const BoekhoudingLayout = ({ children, title, headerActions }: BoekhoudingLayoutProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showJaarMenu, setShowJaarMenu] = useState(false);
  const location = useLocation();

  // Huidig boekjaar uit localStorage
  const [huidigJaar, setHuidigJaar] = useState(() => {
    const saved = localStorage.getItem('boekhouding_jaar');
    return saved ? parseInt(saved) : new Date().getFullYear();
  });

  // Opslaan bij wijziging
  useEffect(() => {
    localStorage.setItem('boekhouding_jaar', huidigJaar.toString());
  }, [huidigJaar]);

  // Sluit jaar menu bij klik buiten
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.logo}`)) {
        setShowJaarMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.classList.toggle('menu-open', !mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const handleJaarSelect = (jaar: number) => {
    setHuidigJaar(jaar);
    setShowJaarMenu(false);
    window.location.reload();
  };

  const isActive = (path: string) => {
    if (path === '/boekhouding') {
      return location.pathname === '/boekhouding';
    }
    return location.pathname.startsWith(path);
  };

  // Jaar opties - gedeelde constante
  const jaarOpties = BOEKJAAR_OPTIES;

  return (
    <div className={styles.boekhouding}>
      {/* Hamburger menu button (mobiel) */}
      <button
        className={styles.mobileMenuToggle}
        onClick={toggleMobileMenu}
        aria-label="Menu"
      >
        <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.hamburgerLineOpen : ''}`}></span>
        <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.hamburgerLineOpen : ''}`}></span>
        <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.hamburgerLineOpen : ''}`}></span>
      </button>

      {/* Overlay voor mobiel menu */}
      <div
        className={`${styles.mobileOverlay} ${mobileMenuOpen ? styles.mobileOverlayOpen : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar */}
      <nav className={`${styles.sidebar} ${mobileMenuOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.logo}>
          <span
            className={styles.jaarKiezer}
            onClick={(e) => { e.stopPropagation(); setShowJaarMenu(!showJaarMenu); }}
          >
            Boekhouding {huidigJaar}
          </span>
          {showJaarMenu && (
            <div className={styles.jaarMenu}>
              {jaarOpties.map(jaar => (
                <button
                  key={jaar}
                  type="button"
                  className={huidigJaar === jaar ? styles.jaarMenuActief : ''}
                  onClick={() => handleJaarSelect(jaar)}
                >
                  {jaar}
                </button>
              ))}
            </div>
          )}
        </div>

        <ul className={styles.navLinks}>
          <li>
            <Link
              to="/boekhouding/dashboard"
              className={isActive('/boekhouding/dashboard') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Dashboard
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding"
              className={isActive('/boekhouding') && location.pathname === '/boekhouding' ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Boekingen
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/leningen"
              className={isActive('/boekhouding/leningen') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Leningen
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/btw"
              className={isActive('/boekhouding/btw') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              BTW Overzicht
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/dc"
              className={isActive('/boekhouding/dc') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              D&C
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/kapitaal"
              className={isActive('/boekhouding/kapitaal') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Kapitaal
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/afschrijvingen"
              className={isActive('/boekhouding/afschrijvingen') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Afschrijvingen
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/exploitatie"
              className={isActive('/boekhouding/exploitatie') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Exploitatie
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/balans"
              className={isActive('/boekhouding/balans') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Balans
            </Link>
          </li>
          <li>
            <Link
              to="/boekhouding/jaarrekening"
              className={isActive('/boekhouding/jaarrekening') ? styles.navLinkActive : ''}
              onClick={closeMobileMenu}
            >
              Jaarrekening
            </Link>
          </li>
        </ul>

        <div className={styles.navBottom}>
          <Link
            to="/boekhouding/instellingen"
            className={styles.btnSettings}
            onClick={closeMobileMenu}
          >
            Instellingen
          </Link>
          <Link
            to="/boekhouding/help"
            className={styles.btnHelp}
            onClick={closeMobileMenu}
          >
            Help
          </Link>
        </div>
      </nav>

      {/* Main content */}
      <main className={styles.content}>
        <header className={styles.topBar}>
          <h1>{title}</h1>
          <div className={styles.headerActions}>
            {headerActions}
          </div>
        </header>

        <div className={styles.mainContent}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default BoekhoudingLayout;
